import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { matchRoute } from './routes';
import { subscribeSeoOverride, type SeoOverride } from './seoStore';
import { SITE_NAME, SITE_LOGO, SITE_DEFAULT_DESCRIPTION, absoluteUrl } from './site';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Centralized, route-aware SEO head manager. On every route change it fully
 * replaces the managed metadata (title, description, keywords, canonical,
 * robots, Open Graph, Twitter Card) so metadata from a previous route cannot
 * leak into the next one.
 */
export default function SeoManager() {
  const { pathname } = useLocation();
  const [override, setOverride] = useState<SeoOverride | null>(null);

  useEffect(() => subscribeSeoOverride(setOverride), []);

  useEffect(() => {
    const route = matchRoute(pathname);
    const indexable = route?.indexable === true;

    const title = override?.title || route?.title || SITE_NAME;
    const description = override?.description || route?.description || SITE_DEFAULT_DESCRIPTION;
    const canonical = absoluteUrl(pathname);
    const image = override?.image || SITE_LOGO;
    const ogType = route?.ogType || 'website';

    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', route?.keywords || '');
    upsertLink('canonical', canonical);
    upsertMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow');

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'ja_JP');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
  }, [pathname, override]);

  return null;
}